import { useState } from 'react';

// One match-note entry as carried by AppState.matchNotes (assembled from the
// match_notes table; tmp_-prefixed ids for optimistic inserts).
export interface MatchNoteEntry {
  id: string;
  body: string;
  authorEmail: string;
  createdAt: string;
}

// "tudor.f · 14:32" — local-part of the author email + UTC clock (match times
// are stored UTC-as-wall-clock; mirror that for the note timestamp).
function noteMeta(email: string, iso: string): string {
  const local = (email ?? '').split('@')[0] ?? '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return local.toUpperCase();
  const h = d.getUTCHours().toString().padStart(2, '0');
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  return `${local.toUpperCase()} · ${h}:${m}`;
}

// Collapsible per-match notes: a "▸ MATCH NOTES (N)" toggle that expands to the
// note history and (for writers) a composer. Used in Results and the schedule
// so volunteer+ can read/leave notes on any match, completed or upcoming. The
// always-open live-match panel in Match Control stays as LiveMatchNotes.
export function CollapsibleMatchNotes({
  notes, onAppend, canWrite, defaultOpen = false,
}: {
  notes: ReadonlyArray<MatchNoteEntry>;
  onAppend: (body: string) => void;
  canWrite: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState('');
  const submit = () => {
    const body = draft.trim();
    if (!body) return;
    onAppend(body);
    setDraft('');
  };
  return (
    <div className="col gap-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn ghost txt-10 fw-bold tracking-3 self-start"
        style={{ padding: '4px 8px', color: 'var(--text-2)' }}
      >
        <span className="mono">{open ? '▾' : '▸'} MATCH NOTES{notes.length ? ` (${notes.length})` : ''}</span>
      </button>
      {open && (
        <div className="col gap-2">
          {notes.length > 0 ? (
            <div className="border r-5 bg-2" style={{ maxHeight: 160, overflowY: 'auto', padding: '6px 0' }}>
              {notes.map((n) => (
                <div key={n.id} className="txt-12" style={{ padding: '4px 10px', lineHeight: 1.4 }}>
                  <span className="mono txt-9 text-3 tracking-2" style={{ marginRight: 6 }}>
                    {noteMeta(n.authorEmail, n.createdAt)}
                  </span>
                  <span className="text-0">{n.body}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="mono txt-10 text-3" style={{ padding: '2px 4px' }}>No notes yet.</div>
          )}
          {canWrite && (
            <div className="row gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder="Add a note… (Enter to send)"
                className="grow txt-12 bg-2 border r-5 text-0"
                style={{ padding: '8px 10px', fontFamily: 'inherit' }}
              />
              <button
                className="btn txt-11"
                onClick={submit}
                disabled={!draft.trim()}
                style={{ padding: '8px 14px', opacity: draft.trim() ? 1 : 0.5 }}
              >
                <span className="mono">SEND ▸</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
